const mounted = new WeakMap();
let cards;

// Store-style previews (?view=style-<style>): the recommendations endpoint ignores the view, so its cards come back in
// the store's own style. Swap each card for the previewed style's card (the saved-product-card route honours the view).
// Normal storefront pages keep the single request.
async function restyleForPreview(content, signal) {
  const preview = /^(?:home|style)-([a-z]+)$/.exec(new URL(location.href).searchParams.get('view') || document.documentElement.dataset.previewSuffix || '');
  if (!preview) return;
  const root = (globalThis.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  await Promise.all([...content.querySelectorAll('[data-product-card]')].map(async (card) => {
    const handle = card.dataset.productHandle;
    if (!handle) return;
    try {
      const url = new URL(`${root}products/${encodeURIComponent(handle)}`, location.origin);
      url.searchParams.set('section_id', 'saved-product-card');
      url.searchParams.set('view', `style-${preview[1]}`);
      const response = await fetch(url, { headers: { Accept: 'text/html' }, signal });
      if (!response.ok) return;
      const fresh = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-product-card]');
      if (fresh?.dataset.productHandle === handle) card.replaceWith(fresh);
    } catch { /* keep the card as received */ }
  }));
}

class Recommendations {
  constructor(root) {
    this.root = root;
    this.abort = new AbortController();
    this.loaded = false;
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => { if (entries.some((entry) => entry.isIntersecting)) { this.observer.disconnect(); this.load(); } }, { rootMargin: '0px 0px 250px 0px' });
      this.observer.observe(root);
    } else this.load();
  }

  async load() {
    if (this.loaded || !this.root.dataset.url) return;
    this.loaded = true;
    try {
      const response = await fetch(this.root.dataset.url, { signal: this.abort.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = [...doc.querySelectorAll('[data-product-recommendations]')].find((node) => node.dataset.recommendationsId === this.root.dataset.recommendationsId);
      const content = incoming?.querySelector('[data-recommendations-content]');
      if (!content) throw new Error('Recommendation section missing');
      await restyleForPreview(content, this.abort.signal);
      const destination = this.root.querySelector('[data-recommendations-content]');
      destination.replaceWith(content);
      if (!content.querySelector('[data-product-card]') && this.root.dataset.emptyBehavior === 'HIDE') { this.root.hidden = true; return; }
      if (content.querySelector('[data-product-card]')) {
        if (!document.querySelector('link[data-product-card-styles]')) {
          const link = document.createElement('link');
          link.rel = 'stylesheet'; link.href = this.root.dataset.cardCss; link.dataset.productCardStyles = 'true';
          document.head.append(link);
        }
        cards ||= await import('./product-card.js');
        cards.mountProductCards(content);
      }
    } catch (error) {
      if (!this.abort.signal.aborted) this.root.hidden = true;
    }
  }

  destroy() {
    this.abort.abort();
    this.observer?.disconnect();
    cards?.unmountProductCards(this.root);
    mounted.delete(this.root);
  }
}

function rootsWithin(scope) { return [...(scope.matches?.('[data-product-recommendations]') ? [scope] : []), ...scope.querySelectorAll('[data-product-recommendations]')]; }
function mount(scope = document) { for (const root of rootsWithin(scope)) if (!mounted.has(root)) mounted.set(root, new Recommendations(root)); }
function unmount(scope) { for (const root of rootsWithin(scope)) mounted.get(root)?.destroy(); }
if (typeof document !== 'undefined') {
  mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target));
  document.addEventListener('shopify:section:select', (event) => mount(event.target));
}
